import React, { useState, useEffect } from "react";
import {
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Grid,
    Typography,
    IconButton,
    Box,
    Divider,
    Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import Spinner from "../../sections/Spinner";
import { toast } from "react-toastify";

export const industryCategories = {
    "Construction & Development": [
        "Ground-up construction sites",
        "Tenant improvement projects",
        "Public works projects",
        "Infrastructure construction (bridges, utilities, rail)",
        "Residential home builders (Lennar, KB, etc.)",
        "Developers",
        "Demolition sites",
        "Equipment yards",
        "Construction storage yards",
        "Roofing companies",
        "Solar installation companies",
        "Contractors & subcontractors",
    ],
    "Logistics, Warehousing & Industrial": [
        "Distribution centers",
        "Logistics companies",
        "Freight forwarding companies",
        "Truck yards & parking facilities",
        "Shipping & receiving depots",
        "Cold storage facilities",
        "Cross-dock operations",
        "Manufacturing plants",
        "Industrial parks",
        "Auto auction yards",
        "Scrap & recycling yards",
        "Container storage facilities",
    ],
    "Retail & Shopping": [
        "Shopping malls",
        "Strip malls/plazas",
        "Big box retail (Target, Walmart, etc.)",
        "Grocery stores",
        "Convenience stores",
        "Jewelry stores",
        "Luxury retail boutiques",
        "Cannabis dispensaries",
        "Pawn shops",
    ],
    "Commercial & Corporate": [
        "Office buildings",
        "Corporate campuses",
        "Business parks",
        "Shared workspaces (WeWork type)",
        "Tech companies",
        "Call centers",
    ],
    "Residential & Housing": [
        "HOAs",
        "Apartment complexes",
        "Condominiums",
        "Senior living communities",
        "Gated communities",
        "Student housing",
        "Transitional housing facilities",
        "Homeless shelters",
    ],
    Healthcare: [
        "Hospitals",
        "Urgent care centers",
        "Clinics",
        "Medical office buildings",
        "Behavioral health centers",
        "Rehab facilities",
        "Dialysis centers",
        "Pharmacies",
    ],
    "Education & Childcare": [
        "Preschools",
        "Daycares",
        "Elementary schools",
        "Middle & High schools",
        "Private schools",
        "Charter schools",
        "Colleges & universities",
        "Trade schools",
        "Tutoring centers",
    ],
    "Events & Entertainment": [
        "Concerts & festivals",
        "Fairs & carnivals",
        "Conferences",
        "Trade shows",
        "Sporting events",
        "Film/TV production sets",
        "Nightclubs & lounges",
        "Banquet halls",
        "Wedding venues",
        "Private parties & VIP events",
    ],
    "Financial & Professional Services": [
        "Banks & credit unions",
        "Check-cashing centers",
        "Loan offices",
        "Insurance offices",
        "Law firms",
        "Title & escrow offices",
    ],
    "Fuel & Transportation": [
        "Gas stations",
        "Truck stops",
        "Auto dealerships",
        "Auto repair shops",
        "Parking structures & lots",
        "Public transit stations",
        "Taxi/ride-share hubs",
        "Airports (private terminals especially)",
    ],
    "Hospitality & Travel": [
        "Hotels & resorts",
        "Motels",
        "Airbnb/short-term rental buildings",
        "Casinos",
        "Tourism centers",
        "Cruise terminals",
    ],
    "Religious & Community": [
        "Mosques",
        "Churches",
        "Temples",
        "Synagogues",
        "Community centers",
        "Cultural centers",
        "Non-profit offices",
    ],
    "Government & Municipal": [
        "City halls",
        "County offices",
        "Public works yards",
        "DMV facilities",
        "Courthouses",
        "Libraries",
        "Parks & recreation facilities",
        "Utility companies",
        "Public housing authorities",
    ],
    "Specialty & High-Risk": [
        "Cannabis cultivation & distribution",
        "Pharmaceutical storage",
        "Data centers",
        "Precious metal facilities",
        "Laboratories",
        "Research centers",
    ],
    "Emergency Contracts": [
        "Disaster relief sites",
        "Evacuation shelters",
        "Incident command centers",
        "Insurance claim sites after disasters",
    ],
    "Vacant / Distressed Property": [
        "Foreclosed homes",
        "REO properties",
        "Vacant commercial buildings",
        "Development land",
        "Abandoned properties",
    ],
    "Property Management": [
        "Property management companies",
        "Facility management firms",
        "Real estate investment groups",
        "Asset protection agencies",
    ],
};

const periodValues = [
    { value: "none", label: "None" },
    { value: "everyday", label: "Everyday" },
    { value: "once_a_week", label: "Once a Week" },
    { value: "twice_a_week", label: "Twice a Week" },
    { value: "once_a_month", label: "Once a Month" },
    { value: "twice_a_month", label: "Twice a Month" },
];

const styles = {
    sectionHeader: {
        backgroundColor: "#f5f5f5",
        padding: "10px",
        marginTop: "20px",
        marginBottom: "15px",
        fontWeight: "bold",
        borderRadius: "4px",
        borderLeft: "4px solid #084e8a",
    },
    fileInput: {
        display: "none",
    },
    fileList: {
        marginTop: "10px",
    },
    fileItem: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "5px 10px",
        backgroundColor: "#f9f9f9",
        marginBottom: "5px",
        borderRadius: "4px",
        fontSize: "0.9rem",
    },
};

const IndustryCrmForm = ({ open, onClose, initialData, onSubmit, loading, readOnly }) => {
    const [formData, setFormData] = useState({
        companyName: "",
        industryCategory: null,
        subCategory: null,
        address: "",
        city: "",
        website: "",
        yearsInBusiness: "",
        contactName: "",
        jobTitle: "",
        phoneNumber: "",
        email: "",
        department: "",
        guardType: "",
        hours: "",
        patrolOrStandingPost: "",
        temporaryOrPermanent: "",
        startDate: null,
        siteRiskLevel: "",
        leadSource: "",
        outreachAttempts: "",
        meetingDate: null,
        proposalSent: false,
        decisionMakerIdentified: false,
        status: "",
        relationshipHistory: "",
        pricingDiscussed: "",
        competitorsMentioned: "",
        reminderDate: null,
        reminderDuration: "",
        reminderComment: "",
    });

    const [attachments, setAttachments] = useState({
        contracts: [],
        ndas: [],
        postOrders: [],
        photos: [],
        complianceDocs: [],
    });

    useEffect(() => {
        return () => {
            Object.values(attachments).forEach(files => {
                files.forEach(file => {
                    if (file.preview) URL.revokeObjectURL(file.preview);
                });
            });
        };
    }, [attachments]);



    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                startDate: initialData.startDate ? dayjs(initialData.startDate) : null,
                meetingDate: initialData.meetingDate
                    ? dayjs(initialData.meetingDate)
                    : null,
                reminderDate: initialData.reminderTime ? dayjs(initialData.reminderTime) : null,
                reminderDuration: initialData.reminderDuration || "",
                reminderComment: initialData.reminderComment || "",
            });

            if (initialData.attachments) {
                const processedAttachments = {
                    contracts: [],
                    ndas: [],
                    postOrders: [],
                    photos: [],
                    complianceDocs: [],
                };

                const getFileType = (url) => {
                    const extension = url.split('.').pop().toLowerCase();
                    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
                        return `image/${extension === 'jpg' ? 'jpeg' : extension}`;
                    }
                    if (extension === 'pdf') {
                        return 'application/pdf';
                    }
                    return 'application/octet-stream';
                };

                const getFileName = (url) => {
                    try {
                        return decodeURIComponent(url.split('/').pop());
                    } catch (e) {
                        return url.split('/').pop();
                    }
                };

                Object.keys(processedAttachments).forEach((key) => {
                    if (initialData.attachments[key] && Array.isArray(initialData.attachments[key])) {
                        processedAttachments[key] = initialData.attachments[key].map(url => ({
                            preview: url,
                            name: getFileName(url),
                            type: getFileType(url),
                            isExisting: true
                        }));
                    }
                });

                setAttachments(processedAttachments);
            }
        } else {
            setFormData({
                companyName: "",
                industryCategory: null,
                subCategory: null,
                address: "",
                city: "",
                website: "",
                yearsInBusiness: "",
                contactName: "",
                jobTitle: "",
                phoneNumber: "",
                email: "",
                department: "",
                guardType: "",
                hours: "",
                patrolOrStandingPost: "",
                temporaryOrPermanent: "",
                startDate: null,
                siteRiskLevel: "",
                leadSource: "",
                outreachAttempts: "",
                meetingDate: null,
                proposalSent: false,
                decisionMakerIdentified: false,
                status: "",
                relationshipHistory: "",
                pricingDiscussed: "",
                competitorsMentioned: "",
                reminderDate: null,
                reminderDuration: "",
                reminderComment: "",
            });
            setAttachments({
                contracts: [],
                ndas: [],
                postOrders: [],
                photos: [],
                complianceDocs: [],
            });
        }
    }, [initialData, open]);



    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleAutocompleteChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === 'industryCategory' ? { subCategory: null } : {})
        }));
    };

    const handleDateChange = (name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e, type) => {
        const files = Array.from(e.target.files).map(file => Object.assign(file, {
            preview: URL.createObjectURL(file)
        }));
        setAttachments((prev) => ({
            ...prev,
            [type]: [...prev[type], ...files],
        }));
    };

    const removeFile = (type, index) => {
        setAttachments((prev) => {
            const newFiles = [...prev[type]];
            URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return { ...prev, [type]: newFiles };
        });
    };

    const handleSubmit = () => {


        const data = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key] === null || formData[key] === undefined) return;
            if (key === 'startDate' || key === 'meetingDate') {
                if (formData[key]) data.append(key, formData[key].format('YYYY-MM-DD'));
            } else if (key === 'reminderDate') {
                if (formData[key]) data.append('reminderTime', formData[key].format('YYYY-MM-DD hh:mm A'));
            } else {
                data.append(key, formData[key]);
            }
        });

        // Append files
        Object.keys(attachments).forEach(key => {
            attachments[key].forEach(file => {
                data.append(key, file);
            });
        });

        onSubmit(data);
    };

    const subCategories = formData.industryCategory
        ? industryCategories[formData.industryCategory] || []
        : [];

    const renderFilePreview = (file, index, type) => {
        const isImage = file.type?.startsWith('image/');
        const isPdf = file.type === 'application/pdf';

        return (
            <Box
                key={index}
                sx={{
                    position: 'relative',
                    width: 100,
                    height: 100,
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    bgcolor: '#f9f9f9',
                    mr: 1,
                    mb: 1
                }}
            >
                {!readOnly && (
                    <IconButton
                        size="small"
                        onClick={() => removeFile(type, index)}
                        sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'rgba(255,255,255,0.8)',
                            padding: '2px',
                            zIndex: 1,
                            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                        }}
                    >
                        <CloseIcon fontSize="small" color="error" />
                    </IconButton>
                )}

                {isImage ? (
                    <img
                        src={file.preview}
                        alt="preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                ) : (
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                        <i className={`fas ${isPdf ? 'fa-file-pdf' : 'fa-file-alt'} fa-2x`} style={{ color: isPdf ? '#dc3545' : '#6c757d' }}></i>
                        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontSize: '0.65rem', lineHeight: 1.1, wordBreak: 'break-all' }}>
                            {file.name.length > 20 ? file.name.substring(0, 18) + '...' : file.name}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    if (!open) return null;

    return (
        <div className="popupOuter">
            <div className="popupInner" style={{ maxWidth: "900px", width: "95%" }}>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
                <div className="popupHeader">
                    <h3>{readOnly ? "View Industry CRM Record" : (initialData ? "Edit" : "Add") + " Industry CRM Record"}</h3>
                </div>
                <div className="popupBody py-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                    <Typography style={styles.sectionHeader}>Company Profile</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Company Name"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={Object.keys(industryCategories)}
                                value={formData.industryCategory}
                                onChange={(event, newValue) => handleAutocompleteChange('industryCategory', newValue)}
                                onInputChange={(event, newInputValue) => handleAutocompleteChange('industryCategory', newInputValue)}
                                freeSolo
                                disabled={readOnly}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Industry Category"
                                        size="small"
                                        fullWidth
                                        helperText={!readOnly ? "Select from list or type custom value" : ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={subCategories}
                                value={formData.subCategory}
                                onChange={(event, newValue) => handleAutocompleteChange('subCategory', newValue)}
                                onInputChange={(event, newInputValue) => handleAutocompleteChange('subCategory', newInputValue)}
                                disabled={readOnly || !formData.industryCategory}
                                freeSolo
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Sub-category"
                                        size="small"
                                        fullWidth
                                        helperText={!readOnly ? "Select from list or type custom value" : ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Website"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Years in business"
                                name="yearsInBusiness"
                                value={formData.yearsInBusiness}
                                onChange={handleChange}
                                size="small"
                                type="number"
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>

                    <Typography style={styles.sectionHeader}>Contact Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Primary Contact Name"
                                name="contactName"
                                value={formData.contactName}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Job Title"
                                name="jobTitle"
                                value={formData.jobTitle}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phoneNumber"
                                value={formData.phoneNumber}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Department</InputLabel>
                                <Select
                                    name="department"
                                    value={formData.department}
                                    label="Department"
                                    onChange={handleChange}
                                    disabled={readOnly}
                                >
                                    <MenuItem value="Operations">Operations</MenuItem>
                                    <MenuItem value="Property Mgmt">Property Mgmt</MenuItem>
                                    <MenuItem value="Security">Security</MenuItem>
                                    <MenuItem value="Facilities">Facilities</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Typography style={styles.sectionHeader}>
                        Security Need Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Guard type</InputLabel>
                                <Select
                                    name="guardType"
                                    value={formData.guardType}
                                    label="Guard type"
                                    onChange={handleChange}
                                    disabled={readOnly}
                                >
                                    <MenuItem value="unarmed">Unarmed</MenuItem>
                                    <MenuItem value="armed">Armed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Hours</InputLabel>
                                <Select
                                    name="hours"
                                    value={formData.hours}
                                    label="Hours"
                                    onChange={handleChange}
                                    disabled={readOnly}
                                >
                                    <MenuItem value="static">Static</MenuItem>
                                    <MenuItem value="roving">Roving</MenuItem>
                                    <MenuItem value="24-7">24-7</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Patrol vs standing post</InputLabel>
                                <Select
                                    name="patrolOrStandingPost"
                                    value={formData.patrolOrStandingPost}
                                    label="Patrol vs standing post"
                                    onChange={handleChange}
                                    disabled={readOnly}
                                >
                                    <MenuItem value="Patrol">Patrol</MenuItem>
                                    <MenuItem value="Standing Post">Standing Post</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Temporary or long-term</InputLabel>
                                <Select
                                    name="temporaryOrPermanent"
                                    value={formData.temporaryOrPermanent}
                                    label="Temporary or long-term"
                                    onChange={handleChange}
                                    disabled={readOnly}
                                >
                                    <MenuItem value="Temporary">Temporary</MenuItem>
                                    <MenuItem value="Long-term">Long-term</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Start Date"
                                    value={formData.startDate}
                                    onChange={(val) => handleDateChange("startDate", val)}
                                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    disabled={readOnly}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Site risk level"
                                name="siteRiskLevel"
                                value={formData.siteRiskLevel}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>

                    <Typography style={styles.sectionHeader}>Sales Tracking</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Lead source"
                                name="leadSource"
                                value={formData.leadSource}
                                onChange={handleChange}
                                size="small"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Outreach attempts"
                                name="outreachAttempts"
                                value={formData.outreachAttempts}
                                onChange={handleChange}
                                size="small"
                                type="number"
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Meeting date"
                                    value={formData.meetingDate}
                                    onChange={(val) => handleDateChange("meetingDate", val)}
                                    slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    disabled={readOnly}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Proposal sent?</InputLabel>
                                <Select
                                    name="proposalSent"
                                    value={formData.proposalSent}
                                    label="Proposal sent?"
                                    onChange={handleChange}
                                    disabled={readOnly}
                                >
                                    <MenuItem value={true}>Yes</MenuItem>
                                    <MenuItem value={false}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Decision maker identified?</InputLabel>
                                <Select
                                    name="decisionMakerIdentified"
                                    value={formData.decisionMakerIdentified}
                                    label="Decision maker identified?"
                                    onChange={handleChange}
                                    disabled={readOnly}
                                >
                                    <MenuItem value={true}>Yes</MenuItem>
                                    <MenuItem value={false}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    name="status"
                                    value={formData.status}
                                    label="Status"
                                    onChange={handleChange}
                                    disabled={readOnly}
                                >
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="closed">Closed</MenuItem>
                                    <MenuItem value="lost">Lost</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Typography style={styles.sectionHeader}>Notes</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Relationship history"
                                name="relationshipHistory"
                                value={formData.relationshipHistory}
                                onChange={handleChange}
                                size="small"
                                multiline
                                rows={2}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Pricing discussed"
                                name="pricingDiscussed"
                                value={formData.pricingDiscussed}
                                onChange={handleChange}
                                size="small"
                                multiline
                                rows={2}
                                disabled={readOnly}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Competitors mentioned"
                                name="competitorsMentioned"
                                value={formData.competitorsMentioned}
                                onChange={handleChange}
                                size="small"
                                multiline
                                rows={2}
                                disabled={readOnly}
                            />
                        </Grid>
                    </Grid>

                    <Typography style={styles.sectionHeader}>Reminder Details</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker
                                    label="Reminder Date & Time"
                                    value={formData.reminderDate}
                                    onChange={(newValue) => handleDateChange("reminderDate", newValue)}
                                    disabled={readOnly}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={periodValues}
                                getOptionLabel={(option) => option.label || (periodValues.find(p => p.value === option)?.label || option)}
                                value={periodValues.find(p => p.value === formData.reminderDuration) || null}
                                onChange={(e, newValue) => handleAutocompleteChange("reminderDuration", newValue?.value || "")}
                                disabled={readOnly}
                                renderInput={(params) => (
                                    <TextField {...params} label="Frequency" size="small" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Reminder Comment"
                                name="reminderComment"
                                multiline
                                rows={2}
                                value={formData.reminderComment}
                                onChange={handleChange}
                                disabled={readOnly}
                                size="small"
                            />
                        </Grid>
                    </Grid>

                    <Typography style={styles.sectionHeader}>Attachments</Typography>
                    {['contracts', 'ndas', 'postOrders', 'photos', 'complianceDocs'].map(type => (
                        <Box key={type} mb={2}>
                            <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>{type.replace(/([A-Z])/g, ' $1').trim()}</Typography>
                            {!readOnly && (
                                <Button variant="outlined" component="label" size="small" sx={{ mt: 1 }}>
                                    Upload
                                    <input hidden accept="*/*" multiple type="file" onChange={(e) => handleFileChange(e, type)} />
                                </Button>
                            )}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
                                {attachments[type].map((file, index) => renderFilePreview(file, index, type))}
                            </Box>
                        </Box>
                    ))}

                </div>

                <div className="popupFooter">
                    {!readOnly && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{ mr: 2 }}
                        >
                            {loading ? <Spinner /> : "Submit"}
                        </Button>
                    )}
                    <Button variant="outlined" color="error" onClick={onClose}>
                        {readOnly ? "Close" : "Cancel"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default IndustryCrmForm;
